import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { AppShell } from '@/shared/ui/app-shell'
import { useTelegram, useBackButton, useMainButton, useSecondaryButton } from '@/shared/lib/telegram'
import { requireAuth } from '@/shared/lib/router'
import { isAuthenticated } from '@/shared/lib/auth'
import { useDrinkHistory, useUpdateDrinkHistoryEntry } from '@/features/drink-history/api/use-drink-history'
import { useToast, ToastContainer } from '@/shared/ui/toast'
import { DiaryForm, type DiaryFormData } from '@/widgets/diary-form'

// Цвета кнопок для разных тем
const BUTTON_COLORS = {
  light: {
    main: { color: '#C73B0D', textColor: '#FFFFFF' },
    secondary: { color: '#E8DFD8', textColor: '#1A1814' },
  },
  dark: {
    main: { color: '#E84D1C', textColor: '#FFFFFF' },
    secondary: { color: '#2D2A26', textColor: '#E8DFD8' },
  },
}

export const Route = createFileRoute('/tea-diary/edit/$entryId')({
  component: TeaDiaryEditPage,
})

/**
 * Страница: Редактирование записи в чайном дневнике
 * 
 * Нативные кнопки Telegram:
 *   - BackButton → в дневник
 *   - SecondaryButton "Отмена" → в дневник
 *   - MainButton "Сохранить" (активна только при наличии изменений)
 */
export function TeaDiaryEditPage() {
  const { entryId } = Route.useParams()
  const { initData, hapticFeedback, webApp } = useTelegram()
  const navigate = useNavigate()
  const toast = useToast()
  const updateEntry = useUpdateDrinkHistoryEntry()

  // Определяем тему для цветов кнопок
  const theme = webApp?.colorScheme || 'light'
  const colors = BUTTON_COLORS[theme]
  
  // Навигация в дневник
  const goToDiary = useCallback(() => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/tea-diary' })
  }, [navigate, hapticFeedback])

  // Нативная BackButton → в дневник
  useBackButton(true, goToDiary)

  // Auth check (before hooks that depend on data)
  if (!isAuthenticated(initData)) {
    requireAuth(initData)
    return null
  }

  const { data } = useDrinkHistory({ limit: 100 })
  
  const entry = useMemo(() => {
    return data?.data?.find(e => e.id === entryId)
  }, [data, entryId])

  const [formData, setFormData] = useState<DiaryFormData>({
    rating: null,
    notes: '',
    photoUrl: null,
    brewingParams: {},
  })
  
  const [originalData, setOriginalData] = useState<DiaryFormData | null>(null)

  // Заполняем форму данными записи
  useEffect(() => {
    if (entry && !originalData) {
      const data: DiaryFormData = {
        rating: entry.rating ?? null,
        mood: entry.mood || null,
        brewingMethod: entry.brewingMethod || null,
        notes: entry.notes || '',
        userPhotoUrl: entry.userPhotoUrl || null,
        brewingParams: entry.brewingParams ? {
          temperature: entry.brewingParams.temperature,
          brewingTime: entry.brewingParams.time,
          teaAmount: entry.brewingParams.teaAmount,
          waterAmount: entry.brewingParams.waterAmount,
        } : {},
      }
      setFormData(data)
      setOriginalData(data)
    }
  }, [entry, originalData])

  // Проверка изменений
  const hasChanges = useMemo(() => {
    if (!originalData) return false
    return JSON.stringify(formData) !== JSON.stringify(originalData)
  }, [formData, originalData])

  const handleSave = useCallback(async () => {
    if (!hasChanges) return

    hapticFeedback.impactOccurred('medium')

    try {
      const brewingParams = (
        formData.brewingParams.temperature ||
        formData.brewingParams.brewingTime ||
        formData.brewingParams.teaAmount ||
        formData.brewingParams.waterAmount
      ) ? {
        temperature: formData.brewingParams.temperature,
        time: formData.brewingParams.brewingTime,
        teaAmount: formData.brewingParams.teaAmount,
        waterAmount: formData.brewingParams.waterAmount,
      } : undefined

      await updateEntry.mutateAsync({
        entryId,
        rating: formData.rating ?? undefined,
        mood: formData.mood || undefined,
        brewingMethod: formData.brewingMethod || undefined,
        notes: formData.notes.trim() || undefined,
        brewingParams,
        userPhotoUrl: formData.userPhotoUrl,
      })

      hapticFeedback.notificationOccurred('success')
      toast.success('Запись обновлена')
      
      setTimeout(() => {
        navigate({ to: '/tea-diary' })
      }, 400)
    } catch (err) {
      hapticFeedback.notificationOccurred('error')
      toast.error('Ошибка сохранения')
    }
  }, [entryId, formData, hasChanges, updateEntry, navigate, hapticFeedback, toast])

  const handleFormChange = useCallback((updates: Partial<DiaryFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }, [])
  
  // Нативная SecondaryButton "Отмена"
  useSecondaryButton({
    text: 'Отмена',
    onClick: goToDiary,
    color: colors.secondary.color,
    textColor: colors.secondary.textColor,
  })
  
  // Нативная MainButton "Сохранить"
  useMainButton({
    text: 'Сохранить',
    onClick: handleSave,
    color: colors.main.color,
    textColor: colors.main.textColor,
    isActive: hasChanges,
    isLoading: updateEntry.isPending,
  })

  if (!entry) {
    return (
      <AppShell showBottomNav={false} showSideNav>
        <div className="flex flex-col flex-1 items-center justify-center bg-paper dark:bg-dark-bg">
          <div className="w-10 h-10 border-2 border-primary dark:border-primary-light border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Загрузка...</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell showBottomNav={false} showSideNav>
      <div className="flex flex-col flex-1 bg-paper dark:bg-dark-bg h-full">
        {/* Header */}
        <header
          className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-silk dark:border-dark-border bg-paper/95 dark:bg-dark-bg/95 backdrop-blur-xl"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top, 0px))' }}
        >
          <button
            type="button"
            onClick={goToDiary}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-silk/60 dark:hover:bg-dark-elevated transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-ink dark:text-white" />
          </button>
          <h1 className="text-lg font-semibold text-ink dark:text-white">Редактирование</h1>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <DiaryForm
            data={formData}
            onChange={handleFormChange}
            teaInfo={{
              name: entry.teaName || 'Чай',
              date: new Date(entry.drankAt).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }),
              imageUrl: entry.userPhotoUrl || entry.photoUrl || undefined,
            }}
            showPhoto={true}
            compact={false}
          />
          <div className="bottom-nav-spacer" />
        </div>
      </div>

      <ToastContainer
        toasts={toast.toasts}
        onRemove={toast.remove}
        onHapticFeedback={(type) => {
          if (type === 'success') hapticFeedback.notificationOccurred('success')
          else if (type === 'error') hapticFeedback.notificationOccurred('error')
          else hapticFeedback.impactOccurred('light')
        }}
      />
    </AppShell>
  )
}
